import type { GetPrizesOrderByType } from "@jti/rest-api-types";

import { type ColorType, type LimitedOfferLabels } from "../ItemsList/ItemsList.types";
import { ALL_POINTS_RANGES_VALUE, type PointsRangeValue } from "../SimplifiedPrizesFilters/PointsRange";
import type { SortByValue } from "../SimplifiedPrizesFilters/SortBy";
import type { SimplifiedPrizesActiveFilters } from "../SimplifiedPrizesV2";

const isLimitedOfferEnabled = (timeTo: string | undefined, { oneDayLabel, daysLabel }: LimitedOfferLabels) => {
  return !!timeTo && !!oneDayLabel && !!daysLabel;
};

const getPrizeColor = (colorFromProps: ColorType, itemColor?: string) => {
  return itemColor === "white" || itemColor === "black" ? itemColor : colorFromProps;
};

const validateTierRestriction = (tier?: string, levels?: string[]) => {
  return tier && levels?.length ? !levels.some((level) => level.toLowerCase() === tier.toLowerCase()) : false;
};

type PrizesSortQuery = { orderBy?: GetPrizesOrderByType; sort?: "asc" | "desc" };

const SORT_BY_QUERY_MAP: Record<SortByValue, PrizesSortQuery> = {
  "price-low-to-high": { orderBy: "points", sort: "asc" },
  "price-high-to-low": { orderBy: "points", sort: "desc" },
  "name-asc": { orderBy: "name", sort: "asc" },
  "name-desc": { orderBy: "name", sort: "desc" },
  "newest-first": { orderBy: "created_at", sort: "desc" },
  "expiring-soon": { orderBy: "expiration", sort: "asc" },
};

const getSortByQuery = (value?: SortByValue): PrizesSortQuery => (value ? SORT_BY_QUERY_MAP[value] : {});

type PrizesPointsRangeQuery = { minPoints?: number; maxPoints?: number };

const getPointsRangeQuery = (value?: PointsRangeValue): PrizesPointsRangeQuery => {
  if (!value || value === ALL_POINTS_RANGES_VALUE) return {};

  if (value.endsWith("+")) {
    const min = Number.parseInt(value, 10);
    return Number.isNaN(min) ? {} : { minPoints: min };
  }

  const [minRaw, maxRaw] = value.split("-");
  const minPoints = Number.parseInt(minRaw, 10);
  const maxPoints = Number.parseInt(maxRaw, 10);

  return {
    minPoints: Number.isNaN(minPoints) ? undefined : minPoints,
    maxPoints: Number.isNaN(maxPoints) ? undefined : maxPoints,
  };
};

// Subcategory ids are `${parentCategoryId}:${label}`; the query needs the labels only.
const getSubcategoriesQuery = (selectedSubcategoryIds: string[]): string[] | undefined => {
  if (!selectedSubcategoryIds.length) return undefined;

  const labels = selectedSubcategoryIds.map((id) => id.split(":")[1] || id);
  return [labels.join(", ")];
};

const getActiveFiltersCount = (activeFilters: SimplifiedPrizesActiveFilters): number => {
  const { category, availableForMe, clubPloomTiers, selectedSubcategoryIds, pointsRangeValue } = activeFilters;

  const isCategoryActive = category.categoryKey !== "all";
  const isPointsRangeActive = Boolean(pointsRangeValue) && pointsRangeValue !== ALL_POINTS_RANGES_VALUE;

  return (
    Number(isCategoryActive) +
    Number(availableForMe) +
    Number(isPointsRangeActive) +
    clubPloomTiers.length +
    selectedSubcategoryIds.length
  );
};

export {
  getActiveFiltersCount,
  getPointsRangeQuery,
  getPrizeColor,
  getSortByQuery,
  getSubcategoriesQuery,
  isLimitedOfferEnabled,
  validateTierRestriction,
};
